import React from 'react';
import { scaleExpr } from './hero/HeroGrid';

interface FloatingLabelProps {
  text: string;
  className?: string;
  /** Vertical placement inside the cell (grid mode). */
  position?: 'top' | 'bottom';
  /** Legacy floating mode: position via `className`, animate with `animate-float`. */
  delay?: number;
  floating?: boolean;
}

/** Padding from the cell's horizontal edge (at BASE_CELL scale). */
const EDGE_INSET = 8;

/**
 * A monospace tag with two placement modes:
 *  - **grid** (default): centered horizontally on a cell, sitting at the top or
 *    bottom edge. Its type/padding scale with the live cell size (via scaleExpr)
 *    so it stays ~one cell wide at any grid density. HeroDecorations positions it.
 *  - **floating** (legacy, when `delay`/`floating` given): positioned by the
 *    caller via `className` at a fixed size and gently floated — used outside the
 *    hero grid (e.g. CodeSection).
 */
const FloatingLabel: React.FC<FloatingLabelProps> = ({
  text,
  className = '',
  position = 'top',
  delay,
  floating,
}) => {
  const isFloating = floating || delay != null;
  // Longer labels render a notch smaller so they don't dominate the cell.
  const isCompact = text.length > 3;

  let style: React.CSSProperties;
  let sizeClass: string;
  let extraClass: string;

  if (isFloating) {
    // Legacy fixed sizing (outside the grid).
    sizeClass = isCompact
      ? 'text-[9px] tracking-[0.16em] px-2 py-[2px] rounded-sm'
      : 'text-[11px] tracking-[0.2em] px-3 py-1 rounded-sm';
    style = { animationDelay: `${delay ?? 0}s` };
    extraClass = 'animate-float';
  } else {
    // Grid mode: sizes are BASE_CELL-relative px scaled to the live cell, so a
    // tag stays proportional to the (now small) squares.
    const fontBase = isCompact ? 12.5 : 15; // → ~7.5 / 9px at a 72px cell
    const padX = isCompact ? 6 : 7;
    const top =
      position === 'top'
        ? scaleExpr(EDGE_INSET)
        : `calc(var(--grid-cell, 72px) - ${scaleExpr(EDGE_INSET)})`;
    style = {
      left: 'calc(var(--grid-cell, 72px) / 2)',
      top,
      transform: `translate(-50%, ${position === 'top' ? '0' : '-100%'})`,
      fontSize: scaleExpr(fontBase),
      lineHeight: 1,
      letterSpacing: scaleExpr(isCompact ? 1.2 : 1.6),
      paddingLeft: scaleExpr(padX),
      paddingRight: scaleExpr(padX),
      paddingTop: scaleExpr(2.5),
      paddingBottom: scaleExpr(2.5),
      borderRadius: scaleExpr(3.5),
    };
    sizeClass = '';
    extraClass = '';
  }

  return (
    <span
      className={`absolute whitespace-nowrap pointer-events-none select-none hidden md:block font-mono uppercase text-slate-400 border border-slate-200/80 bg-white/60 backdrop-blur-sm z-[1] ${sizeClass} ${extraClass} ${className}`}
      style={style}
    >
      [&nbsp;{text}&nbsp;]
    </span>
  );
};

export default FloatingLabel;
